/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import keysToConstants from 'js/lib/keysToConstants';

import { Dialog, Typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import { getS12nEnrollModalCopies } from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellEnrollCopies';
import {
  generateImpressionForPrePurchaseUpsellExperiment,
  previewPrePurchaseUpsellVariant,
} from 'bundles/coursera-plus-common/utils/prePurchaseUpsellExperimentUtils';
import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import { useEnrollInS12nSubscription } from 'bundles/enroll-common/utils/useEnrollInS12nSubscription';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import PromotionApplicableCheckoutMessage from 'bundles/enroll-modal/components/common/PromotionApplicableCheckoutMessage';
import CourseraPlusPrePurchaseUpsellModal from 'bundles/enroll-modal/components/coursera-plus/prePurchaseUpsell/CourseraPlusPrePurchaseUpsellModal';
import SubscriptionEnrollButton from 'bundles/enroll-modal/components/subscriptions/SubscriptionEnrollButton';
import SubscriptionFooter from 'bundles/enroll-modal/components/subscriptions/SubscriptionFooter';
import usePageData from 'bundles/enroll-modal/data/usePageData';
import { useEnrollModalEventing } from 'bundles/enroll-modal/utils/eventingUtils';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';

const MODAL_TYPES = keysToConstants(['ENROLL', 'ERROR', 'PRE_PURCHASE_UPSELL']);

const styles = {
  dialog: css`
    .cds-Dialog-dialog {
      max-width: 720px !important;
    }
  `,
  header: css`
    padding-bottom: var(--cds-spacing-100);
  `,
  subtitle: css`
    margin-bottom: var(--cds-spacing-400);
  `,
  valueProps: css`
    padding: 0;
    margin: 0;
    list-style-type: none;

    li:first-child {
      padding-top: 0;
    }
  `,
  valuePropListItem: css`
    display: flex;
    align-items: flex-start;
    padding: var(--cds-spacing-100) 0;
  `,
  valuePropHeader: css`
    margin: 0;
  `,
  valuePropSubheader: css`
    padding-top: 4px;
  `,
  checkMark: css`
    flex-shrink: 0;
    margin-right: var(--cds-spacing-100);
  `,
  actions: (onSdp: boolean) => css`
    padding: ${onSdp ? `24px 48px 48px 48px` : `24px 48px 30px 48px`};
  `,
  promoMessage: css`
    padding-bottom: var(--cds-spacing-100);
  `,
};

type PropsFromCaller = {
  onClose: () => void;
  onSdp: boolean;
  isFromS12nSelection?: boolean;
  isPrePurchaseUpsellEligible?: boolean;
};

const SubscriptionEnrollModalV2 = ({
  onClose,
  onSdp,
  isFromS12nSelection = false,
  isPrePurchaseUpsellEligible,
}: PropsFromCaller) => {
  const [activeModal, setActiveModal] = useState<keyof typeof MODAL_TYPES | null>(MODAL_TYPES.ENROLL);
  const [error, setError] = useState<ApiError | undefined>();
  const [enrolling, setEnrolling] = useState(false);
  const isUsingPrePurchaseUpsellFlow = activeModal === MODAL_TYPES.PRE_PURCHASE_UPSELL;
  const { getEnrollModalTrackingRef, trackEnrollModalClose } = useEnrollModalEventing({
    isUsingPrePurchaseUpsellFlow,
  });
  const visibilityTrackingRef = getEnrollModalTrackingRef();
  const isPrePurchaseUpsellEnabled =
    isPrePurchaseUpsellEligible && ['1', '2'].includes(previewPrePurchaseUpsellVariant());
  const { s12n, course, s12nDerivatives, s12nSubscriptionOwnableProduct } = usePageData();
  const { s12nMonthlySkuId, canEnrollThroughS12nSubscriptionFreeTrial, enrollInSubscription } =
    useEnrollInS12nSubscription(isFromS12nSelection, isUsingPrePurchaseUpsellFlow);

  const s12nSubscriptionPrice = s12nSubscriptionOwnableProduct?.price ?? s12nDerivatives?.catalogPrice;
  if (!s12n || !s12nMonthlySkuId || !s12nSubscriptionPrice) {
    return null;
  }

  const { HeadingGroup, Content, Actions } = Dialog;

  const copy = getS12nEnrollModalCopies({
    s12nSubscriptionPrice,
    s12nProductVariant: s12n.productVariant,
    s12n,
    course,
  });

  const handleClose = () => {
    trackEnrollModalClose();
    onClose();
  };

  const handleError = (apiError: ApiError) => {
    setEnrolling(false);
    setActiveModal(MODAL_TYPES.ERROR);
    setError(apiError);
  };

  const proceedToCheckout = () => {
    setEnrolling(true);
    enrollInSubscription().catch(handleError);
  };

  const onClickSubscribeCta = () => {
    if (isPrePurchaseUpsellEligible) {
      generateImpressionForPrePurchaseUpsellExperiment();
    }

    if (isPrePurchaseUpsellEnabled) {
      setActiveModal(MODAL_TYPES.PRE_PURCHASE_UPSELL);
    } else {
      proceedToCheckout();
    }
  };

  const onDeclinePrePurchaseUpsell = (shouldGoToCheckoutOnClose: boolean) => {
    if (shouldGoToCheckoutOnClose) {
      proceedToCheckout();
    } else {
      handleClose();
    }
  };

  const getTitle = () => {
    if (canEnrollThroughS12nSubscriptionFreeTrial) {
      return copy.freeTrialTitle;
    } else {
      return copy.paidTitle;
    }
  };

  const getSubtitle = () => {
    if (onSdp && canEnrollThroughS12nSubscriptionFreeTrial) {
      return null;
    } else if (onSdp && !canEnrollThroughS12nSubscriptionFreeTrial) {
      return copy.paidS12nSubtitle;
    } else if (!onSdp && canEnrollThroughS12nSubscriptionFreeTrial) {
      return copy.freeTrialSubtitle;
    } else {
      return copy.paidCourseSubtitle;
    }
  };

  const onCloseClick = () => {
    handleClose();
    trackEnrollModalClose();
  };

  const getValueProps = () => {
    if (canEnrollThroughS12nSubscriptionFreeTrial) {
      const valueProps = copy.freeTrialValueProps.FREE_TRIAL_LOGGED_IN_BULLETS;
      return (
        <ul css={styles.valueProps}>
          {valueProps.map((valueProp) => (
            <li key={`value-prop-${valueProp}`} css={styles.valuePropListItem}>
              <CheckIcon size="large" color="interactive" css={styles.checkMark} />
              <div>
                <Typography2 component="div" variant="subtitleMedium" css={styles.valuePropHeader}>
                  <FormattedMessage
                    message={valueProp.header}
                    monthlyPrice={
                      <ReactPriceDisplay
                        value={s12nSubscriptionPrice.amount}
                        currency={s12nSubscriptionPrice.currencyCode}
                        hideCurrencyCode={true}
                      />
                    }
                  />
                </Typography2>
                <Typography2 component="div" variant="bodySecondary" css={styles.valuePropSubheader}>
                  {valueProp.subheader}
                </Typography2>
              </div>
            </li>
          ))}
        </ul>
      );
    } else {
      const valueProps = copy.paidValueProps;
      return (
        <ul css={styles.valueProps}>
          {valueProps.map((valueProp) => (
            <li key={`value-prop-${valueProp}`} css={styles.valuePropListItem}>
              <CheckIcon size="large" color="interactive" css={styles.checkMark} />
              <Typography2 component="p">{valueProp}</Typography2>
            </li>
          ))}
        </ul>
      );
    }
  };

  switch (activeModal) {
    case MODAL_TYPES.PRE_PURCHASE_UPSELL:
      return (
        <CourseraPlusPrePurchaseUpsellModal
          onDeclinePrePurchaseUpsell={onDeclinePrePurchaseUpsell}
          onError={handleError}
          courseId={course.id}
          s12nId={s12n.id}
          isS12n={true}
        />
      );
    case MODAL_TYPES.ENROLL:
      return (
        <Dialog
          data-testid="subscription-enroll-modal"
          open
          aria-describedby="dialog-content"
          width="medium"
          variant="standard"
          ref={visibilityTrackingRef}
          sticky="actions"
          onClose={onCloseClick}
        >
          <HeadingGroup css={styles.header}>{getTitle() as string}</HeadingGroup>
          <Content id="dialog-content">
            <div css={styles.promoMessage}>
              <PromotionApplicableCheckoutMessage
                course={course}
                s12n={s12n}
                isFromS12nSelection={isFromS12nSelection}
              />
            </div>
            <Typography2 variant="bodyPrimary" component="div" css={styles.subtitle}>
              {getSubtitle()}
            </Typography2>
            {getValueProps()}
          </Content>
          <Actions css={styles.actions(onSdp)}>
            <SubscriptionEnrollButton
              isFromS12nSelection={isFromS12nSelection}
              showCds={true}
              onClick={onClickSubscribeCta}
              enrolling={enrolling}
            />
            {!onSdp && (
              <div>
                <SubscriptionFooter />
              </div>
            )}
          </Actions>
        </Dialog>
      );
    case MODAL_TYPES.ERROR:
      return <EnrollErrorModal error={error} onClose={handleClose} isFinancialAid={false} isFreeEnroll={false} />;
    default:
      return null;
  }
};

export default SubscriptionEnrollModalV2;
